/**
 * PaginationResultProcessor - Utility for processing pagination results
 *
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 *
 * This utility handles the common post-processing operations that occur after
 * a successful pagination operation, including caching, mapping updates, and
 * state synchronization.
 *
 * 🆕 Phase 0 Height-Index: Also extracts and logs paragraph_heights from WASM output
 * for streaming pagination analysis. See HeightIndexManager for the data consumer.
 */

import type { PageLayout, ParagraphSnapshot, PageWindowResult, ParagraphHeightInfo } from '../../../core/types/Typesetter';
import { logPaginationResult, logSnapshotCached } from '../snapshot';
import { cachePages } from '../cache';
import { PageHashStorer } from '../cache';
import { ParagraphToPageMapBuilder } from '../paragraph';
import { createSnapshotWithIdForCaching } from '../snapshot';
import { WasmLogger } from '../../../../../utils/loggers/WasmLogger';

/**
 * Parameters for processing pagination results
 */
export interface PaginationResultProcessingParams {
  result: PageWindowResult;
  docHash: string;
  startPage: number;
  endPage: number;
  consecutiveWasmCrashes: number;
}

/**
 * State objects that need to be updated during processing
 * Note: Objects are passed by reference and will be mutated
 */
export interface PaginationResultProcessingState {
  pageCache: Map<string, PageLayout>;
  pageHashes: Map<number, string>;
  paragraphToPageMap: Map<string, number[]>;
  cachedParagraphSnapshots: ParagraphSnapshot[];
  currentTotalPages: number;
  consecutiveWasmCrashes: number;
}

/**
 * Process pagination results and update all relevant caches and state
 *
 * This function handles the common post-processing operations that occur after
 * every successful pagination operation:
 * - Logging the result
 * - Caching pages with size limits
 * - Storing page hashes for early termination
 * - Building paragraph-to-page mapping for SpillFrontier calculation
 * - Updating total pages count
 * - Caching paragraph snapshots for incremental updates
 * - Resetting crash counter on success
 *
 * @param params - Pagination result and metadata
 * @param state - State objects to update
 * @returns Updated visible pages filtered to the requested range
 */
export function processPaginationResult(
  params: PaginationResultProcessingParams,
  state: PaginationResultProcessingState
): PageLayout[] {
  const { result, docHash, startPage, endPage } = params;

  // Log the pagination result
  logPaginationResult(params.result);

  // Cache pages with size limits
  cachePages(state.pageCache, params.docHash, params.result.pages, 50);

  // Store page hashes for early termination in future paginations
  PageHashStorer.storePageHashes(params.result.pages, state.pageHashes);

  // Build paragraph-to-page mapping for SpillFrontier calculation
  ParagraphToPageMapBuilder.buildParagraphToPageMap(params.result.pages, state.paragraphToPageMap);

  // Update total pages count
  // 🔥 CRITICAL FIX: Only trust result.total_pages when pagination is COMPLETE (no next_cursor).
  // When next_cursor is present, WASM returned a windowed result where total_pages equals
  // pages.length (the window size), NOT the actual document page count.
  // This was causing currentTotalPages to be reset to 1 after Ctrl+Enter created a new page,
  // which corrupted startPageIndex calculations and caused Page 0 to render all content.
  if (params.result.next_cursor === null || params.result.next_cursor === undefined) {
    // Full pagination complete - trust the total_pages from WASM
    state.currentTotalPages = params.result.total_pages;
  } else {
    // Windowed pagination - preserve existing count or use max to avoid losing pages
    // The existing count is authoritative; only increase if WASM reports more pages
    state.currentTotalPages = Math.max(state.currentTotalPages, params.result.total_pages);
    WasmLogger.pagination.result(params.result.total_pages, params.result.next_cursor, { windowedPagination: true, preservedTotalPages: state.currentTotalPages });
  }

  // Cache the paragraph snapshot for incremental updates
  // 🔥 CRITICAL FIX: Only cache the combined 'para-0' snapshot if we don't already have
  // proper per-paragraph snapshots. Multi-paragraph documents need individual snapshots
  // stored by storeInitialParagraphSnapshots() during document initialization.
  // If we already have per-paragraph snapshots (e.g., 'para-0', 'para-1', 'para-2'),
  // overwriting with a single combined 'para-0' would corrupt the cache.
  const cacheLength = state.cachedParagraphSnapshots.length;
  const hasMultipleSnapshots = cacheLength > 1;
  const hasSingleNonPara0 = cacheLength === 1 && 
    state.cachedParagraphSnapshots.some(s => s.snapshot_id !== 'para-0');
  const hasProperPerParagraphSnapshots = hasMultipleSnapshots || hasSingleNonPara0;
  
  WasmLogger.snapshot.cacheCheck({
    cacheLength,
    hasMultipleSnapshots,
    hasSingleNonPara0,
    hasProperPerParagraphSnapshots,
    snapshotIds: state.cachedParagraphSnapshots.map(s => s.snapshot_id)
  });
  
  if (!hasProperPerParagraphSnapshots) {
    const snapshotWithId = createSnapshotWithIdForCaching(params.result.snapshot, 'para-0');
    if (snapshotWithId) {
      state.cachedParagraphSnapshots.length = 0; // Clear existing
      state.cachedParagraphSnapshots.push(snapshotWithId);
      logSnapshotCached(params.result.snapshot, snapshotWithId.snapshot_id);
    }
  } else {
    WasmLogger.snapshot.preserve(`Preserving ${state.cachedParagraphSnapshots.length} per-paragraph snapshots (not overwriting with combined para-0)`, { count: state.cachedParagraphSnapshots.length });
  }

  // Reset crash counter on successful pagination
  state.consecutiveWasmCrashes = 0;

  // 🆕 Phase 0 Height-Index: Log paragraph heights for streaming pagination analysis
  // This data will be consumed by HeightIndexManager in Phase 1+
  if (params.result.paragraph_heights && params.result.paragraph_heights.length > 0) {
    const heights = params.result.paragraph_heights;
    const totalHeightPx = heights.reduce((sum, h) => sum + h.height_px, 0);

    WasmLogger.layout.paginationInput({
      architecture: 'HEIGHT_INDEX_RECEIVED',
      phase: 'phase_0_instrumentation',
      paragraphCount: heights.length,
      totalHeightPx,
      firstParagraphId: heights[0]?.paragraph_id,
      lastParagraphId: heights[heights.length - 1]?.paragraph_id,
      sampleHeights: heights.slice(0, 5).map(h => ({
        id: h.paragraph_id.substring(0, 8),
        heightPx: h.height_px,
        lineCount: h.line_count
      }))
    });
  }

  // Return visible pages filtered to requested range
  // Note: Using simple slice for now - could use filterAndSortPagesByRange if needed
  return result.pages.slice(startPage, endPage + 1);
}

/**
 * Extract paragraph heights from pagination result (for HeightIndexManager)
 * Returns empty array if not present in result
 */
export function extractParagraphHeights(result: PageWindowResult): ParagraphHeightInfo[] {
  return result.paragraph_heights ?? [];
}
